import type { FilterFacet, FilterFacetOption } from './Filter.types';
import { FACET_ORDER, FIXED_FACET_OPTIONS } from './Filters.constants';

export type FacetLabelResolver = Readonly<{
  /** Label for a presented facet (pill). */
  facet: (groupCode: string) => string;
  /** Label for an option; `undefined` means "no label known" and the code is shown bracketed. */
  option: (compositeCode: string) => string | undefined;
}>;

const groupOf = (compositeCode: string) => compositeCode.split(':')[0];

const NO_ACTIVE_FILTERS: ReadonlySet<string> = new Set();

/**
 * Turns per-option match counts (`compositeCode -> count`) into the facets the gallery
 * presents. `free` / `feature` take their fixed option lists in display order (an option
 * is dropped when no template matches it, so e.g. Magnet only appears on categories that
 * have magnets); every other presented group takes all discovered options of the same
 * underlying group, sorted by label. A selected option is kept even at a count of 0, whether
 * that is because the other groups' selections scope it out or because this category holds no
 * such design at all (a `?pf=` deep link can carry one in). Either way the user must be able to
 * see and clear what they picked, and the pill must read as active. Pure, for testability.
 */
export const buildPresentedFacets = (
  facetOptionCounts: ReadonlyMap<string, number>,
  labels: FacetLabelResolver,
  activeFilters: ReadonlySet<string> = NO_ACTIVE_FILTERS,
): FilterFacet[] => {
  const toOption = (compositeCode: string, count: number): FilterFacetOption => ({
    compositeCode,
    label: labels.option(compositeCode) ?? `[${compositeCode.replace(':', ' ')}]`,
    matchingItemCount: count,
  });

  const isPresented = (compositeCode: string, count: number) => count > 0 || activeFilters.has(compositeCode);

  const facets: FilterFacet[] = [];
  for (const groupCode of FACET_ORDER) {
    const fixed = FIXED_FACET_OPTIONS[groupCode];
    let options: FilterFacetOption[];
    if (fixed) {
      options = fixed.filter(code => isPresented(code, facetOptionCounts.get(code) ?? 0)).map(code => toOption(code, facetOptionCounts.get(code) ?? 0));
    } else {
      const uncountedActive = Array.from(activeFilters)
        .filter(code => groupOf(code) === groupCode && !facetOptionCounts.has(code))
        .map(code => [code, 0] as const);
      options = [...facetOptionCounts.entries(), ...uncountedActive]
        .filter(([code, count]) => groupOf(code) === groupCode && isPresented(code, count))
        .map(([code, count]) => toOption(code, count))
        .sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));
    }
    if (options.length === 0) continue;
    facets.push({ groupCode, label: labels.facet(groupCode), options });
  }
  return facets;
};

/** How many of this facet's options are currently selected. Works for mirrored groups, unlike a group-prefix count. */
export const countActiveOptions = (facet: Pick<FilterFacet, 'options'>, activeFilters: ReadonlySet<string>) =>
  facet.options.filter(option => activeFilters.has(option.compositeCode)).length;
